import { HISTORY_TILE_SIZE } from '../history/kl-history';
import { TKlCanvasLayer } from './kl-canvas';
import { THistoryEntryDataComposed, THistoryEntryLayerTile } from '../history/history.types';
import { BB } from '../../bb/bb';
import { sortLayerMap } from '../history/sort-layer-map';
import { isLayerFill } from '../kl-types';

/**
 * Applies history delta to the project, optimized for performance.
 * note: modifies the canvases in project
 */
export function updateLayersViaComposed(
    layers: TKlCanvasLayer[],
    before: THistoryEntryDataComposed,
    after: THistoryEntryDataComposed,
): TKlCanvasLayer[] {
    const sizeDidChange =
        before.size.width !== after.size.width || before.size.height !== after.size.height;

    return Object.entries(after.layerMap)
        .map(([id, composedAfterLayer]) => {
            let canvas = {} as HTMLCanvasElement;
            let context = {} as CanvasRenderingContext2D;
            const composedBeforeLayer = before.layerMap[id];
            const tilesPerX = Math.ceil(after.size.width / HISTORY_TILE_SIZE);

            if (sizeDidChange || !composedBeforeLayer) {
                // create new canvas
                canvas = BB.canvas(after.size.width, after.size.height);
                context = BB.ctx(canvas);

                composedAfterLayer.tiles.forEach((item, index) => {
                    const x = index % tilesPerX;
                    const y = Math.floor(index / tilesPerX);
                    if (isLayerFill(item)) {
                        context.save();
                        context.fillStyle = item!.fill;
                        context.fillRect(
                            x * HISTORY_TILE_SIZE,
                            y * HISTORY_TILE_SIZE,
                            HISTORY_TILE_SIZE,
                            HISTORY_TILE_SIZE,
                        );
                        context.restore();
                    } else {
                        context.putImageData(
                            item.data,
                            x * HISTORY_TILE_SIZE,
                            y * HISTORY_TILE_SIZE,
                        );
                    }
                });
            } else {
                canvas = layers[composedBeforeLayer.index].canvas;
                context = layers[composedBeforeLayer.index].context;
                composedAfterLayer.tiles.forEach((item, index) => {
                    if (item === composedBeforeLayer.tiles[index]) {
                        // todo more advanced check ^
                        return;
                    }
                    const x = index % tilesPerX;
                    const y = Math.floor(index / tilesPerX);
                    if (isLayerFill(item)) {
                        context.save();
                        context.fillStyle = item.fill;
                        context.clearRect(
                            x * HISTORY_TILE_SIZE,
                            y * HISTORY_TILE_SIZE,
                            HISTORY_TILE_SIZE,
                            HISTORY_TILE_SIZE,
                        );
                        context.fillRect(
                            x * HISTORY_TILE_SIZE,
                            y * HISTORY_TILE_SIZE,
                            HISTORY_TILE_SIZE,
                            HISTORY_TILE_SIZE,
                        );
                        context.restore();
                    } else {
                        context.putImageData(
                            item.data,
                            x * HISTORY_TILE_SIZE,
                            y * HISTORY_TILE_SIZE,
                        );
                    }
                });
            }

            const result: TKlCanvasLayer = {
                id,
                index: composedAfterLayer.index,
                name: composedAfterLayer.name,
                mixModeStr: composedAfterLayer.mixModeStr,
                isVisible: composedAfterLayer.isVisible,
                opacity: composedAfterLayer.opacity,
                canvas,
                context,
            };

            // Restore animation frames if present
            if (composedAfterLayer.frameTiles && composedAfterLayer.frameTiles.length > 0) {
                const frames: HTMLCanvasElement[] = [canvas];
                const frameContexts: CanvasRenderingContext2D[] = [context];
                for (let f = 0; f < composedAfterLayer.frameTiles.length; f++) {
                    const frameTiles = composedAfterLayer.frameTiles[f];
                    const existingLayer = composedBeforeLayer ? layers[composedBeforeLayer.index] : undefined;
                    const existingFrame = existingLayer?.frames?.[f + 1];
                    let fc: HTMLCanvasElement;
                    let fctx: CanvasRenderingContext2D;

                    if (sizeDidChange || !composedBeforeLayer || !existingFrame) {
                        fc = BB.canvas(after.size.width, after.size.height);
                        fctx = BB.ctx(fc);
                        frameTiles.forEach((item: THistoryEntryLayerTile, index: number) => {
                            const x = index % tilesPerX;
                            const y = Math.floor(index / tilesPerX);
                            if (isLayerFill(item)) {
                                fctx.save();
                                fctx.fillStyle = item.fill;
                                fctx.fillRect(x * HISTORY_TILE_SIZE, y * HISTORY_TILE_SIZE, HISTORY_TILE_SIZE, HISTORY_TILE_SIZE);
                                fctx.restore();
                            } else {
                                fctx.putImageData(item.data, x * HISTORY_TILE_SIZE, y * HISTORY_TILE_SIZE);
                            }
                        });
                    } else {
                        fc = existingFrame;
                        fctx = fc.getContext('2d')!;
                        const composedBeforeFrameTiles = composedBeforeLayer.frameTiles?.[f];
                        frameTiles.forEach((item: THistoryEntryLayerTile, index: number) => {
                            if (composedBeforeFrameTiles && item === composedBeforeFrameTiles[index]) {
                                return;
                            }
                            const x = index % tilesPerX;
                            const y = Math.floor(index / tilesPerX);
                            if (isLayerFill(item)) {
                                fctx.save();
                                fctx.fillStyle = item.fill;
                                fctx.clearRect(x * HISTORY_TILE_SIZE, y * HISTORY_TILE_SIZE, HISTORY_TILE_SIZE, HISTORY_TILE_SIZE);
                                fctx.fillRect(x * HISTORY_TILE_SIZE, y * HISTORY_TILE_SIZE, HISTORY_TILE_SIZE, HISTORY_TILE_SIZE);
                                fctx.restore();
                            } else {
                                fctx.putImageData(item.data, x * HISTORY_TILE_SIZE, y * HISTORY_TILE_SIZE);
                            }
                        });
                    }
                    frames.push(fc);
                    frameContexts.push(fctx);
                }
                result.frames = frames;
                result.frameContexts = frameContexts;
            }

            return result;
        })
        .sort(sortLayerMap);
}
